import * as React from 'react';
import {type TextFieldProps} from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Typography from '@mui/material/Typography';
import GoogleMapsDisclaimerPaper from "./GoogleMapsDisclaimer.tsx";
import { useEffect } from "react";
import { fetchGooglePlacePrediction } from "./GooglePlacesAutocompleteConnector.ts";
import {TextField} from "@mui/material";
import { createLogger } from "../../lib/logger.ts";

const logger = createLogger("AddressInput");

const fromGooglePlacePrediction = (prediction: google.maps.places.PlacePrediction): PlacePredictionInternal => {
    return {
        distanceMeters: prediction.distanceMeters,
        mainText: prediction.mainText?.text || null,
        placeId: prediction.placeId,
        secondaryText: prediction.secondaryText?.text || null,
        text: prediction.text.text,
        types: prediction.types,
    };
}

const emptyOptions = [] as PlacePredictionInternal[];
let sessionToken: google.maps.places.AutocompleteSessionToken;

type Props = {
    id?: string;
    label?: string;
    onChange: (value: AddressWithPrediction) => void;
    currentAddress: AddressWithPrediction;
    placeholder?: string;
    renderInput?: (params: TextFieldProps) => React.ReactNode;
};

const AddressInput: React.FC<Props> = ({
    id,
    label,
    onChange,
    currentAddress,
    placeholder,
    renderInput
}) => {
    const reactId = React.useId();
    const [value, setValue] = React.useState<AddressWithPrediction>(currentAddress);
    const [inputValue, setInputValue] = React.useState(currentAddress.address ? currentAddress.address : '');
    const [options, setOptions] = React.useState<PlacePredictionInternal[]>(emptyOptions);

    renderInput = renderInput ? renderInput : (params) => (
        <TextField
            {...params}
            type="text"
            ref={React.createRef<HTMLInputElement>()}
            label={label}
            slots={{ inputLabel: "h3" }}
            variant='outlined'
            placeholder={placeholder}
        />
    );

    function convertGooglePredictionToOptions() {
        sessionToken = sessionToken || new google.maps.places.AutocompleteSessionToken();
        fetchGooglePlacePrediction({
            input: inputValue,
        }, sessionToken, (results?: readonly google.maps.places.PlacePrediction[]) => {
            let newOptions: PlacePredictionInternal[] = [];

            if (results) {
                newOptions = results.map(prediction => (fromGooglePlacePrediction(prediction)));

                if (value.prediction) {
                    newOptions = [
                        value.prediction,
                        ...newOptions.filter((result: PlacePredictionInternal) => result.text !== value.prediction?.text),
                    ];
                }
            } else if (value.prediction) {
                newOptions = [value.prediction];
            }
            setOptions(newOptions);
        });
    }

    useEffect(() => {
        if (inputValue === '') {
            setOptions(value?.prediction ? [value.prediction] : emptyOptions);
            return;
        }

        convertGooglePredictionToOptions();

        return;
    }, [value, inputValue]);

    return (
        <Autocomplete
            getOptionLabel={(option) => option.text}
            slots={{ paper: GoogleMapsDisclaimerPaper }}
            forcePopupIcon
            id={id || reactId}
            options={options}
            autoComplete
            autoHighlight
            includeInputInList
            value={value.prediction}
            fullWidth
            filterOptions={(options) => options}
            noOptionsText="No locations"
            onChange={(_, newValue: PlacePredictionInternal | null) => {
                if (newValue) {
                    setOptions([newValue, ...options]);
                    setValue({ address: newValue.text, prediction: newValue });
                    onChange({ address: newValue.text, prediction: newValue });
                }
            }}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={renderInput}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                logger.debug('RenderOption', { key, optionProps });
                return (
                    <li key={key} {...optionProps}>
                        <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        <Typography sx={{ color: 'text.secondary' }}>{option.text}</Typography>
                    </li>
                );
            }}
        />
    );
}

export default AddressInput;